import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Camera, CheckCheck, ExternalLink, RefreshCw, ImageOff } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { uploadUrl } from "../lib/uploads";

/*
 * PortalPhotos — review queue for client-uploaded gallery items.
 *
 * Wave 19 #82 — every photo a customer uploads via the public portal
 * lands here as an unseen item. Staff acknowledge each item (or all at
 * once) so the rose-red priority banner clears.
 */

function _fmt(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
}

export default function PortalPhotos() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: q }, { data: cs }] = await Promise.all([
        api.get("/priority-queue"),
        api.get("/customers").catch(() => ({ data: [] })),
      ]);
      const list = (q?.portal_uploads?.items || []).slice();
      // Newer first
      list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setItems(list);
      const idx = {};
      (cs || []).forEach((c) => { idx[c.id] = c; });
      setCustomers(idx);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load portal photos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markSeen = async (item) => {
    setBusyId(item.id);
    try {
      await api.post(`/gallery-items/${item.id}/mark-seen`);
      setItems((rows) => rows.filter((r) => r.id !== item.id));
      toast.success("Marked as seen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as seen.");
    } finally {
      setBusyId("");
    }
  };

  const markAllSeen = async () => {
    if (items.length === 0) return;
    setBusyId("__all__");
    try {
      await api.post("/gallery-items/mark-seen-bulk", {
        item_ids: items.map((i) => i.id),
      });
      setItems([]);
      toast.success("All marked as seen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark all as seen.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-photos-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
            <Camera className="w-7 h-7 text-rose-300" />
            Client photo uploads
          </h1>
          <p className="text-xs text-[#a0aabf] mt-1">
            Photos uploaded via the public customer portal. Review each one and mark it seen so the team alert clears.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="portal-photos-refresh"
            disabled={loading}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={markAllSeen}
            disabled={items.length === 0 || busyId === "__all__"}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
            data-testid="portal-photos-markall"
          >
            <CheckCheck className="w-3 h-3" /> Mark all seen
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-rose-200 border border-rose-500/30 bg-rose-500/10 rounded px-3 py-2">
          {err}
        </div>
      )}

      {loading ? (
        <div className="hv-card p-6 text-sm text-[#a0aabf]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hv-card p-10 text-center" data-testid="portal-photos-empty">
          <ImageOff className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
          <h3 className="font-display text-xl font-bold mb-1">All caught up</h3>
          <p className="text-xs text-[#a0aabf]">No unseen client photos right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => {
            const c = customers[it.customer_id];
            const isImage = (it.media_type === "image") || /^image\//i.test(it.content_type || "");
            return (
              <div
                key={it.id}
                className="hv-card p-3 flex flex-col gap-2"
                data-testid={`portal-photo-${it.id}`}
              >
                <div className="aspect-video w-full bg-black/40 border border-white/5 rounded-md overflow-hidden flex items-center justify-center">
                  {isImage && it.file_url ? (
                    <img src={uploadUrl(it.file_url) || it.file_url} alt="" className="object-contain max-h-full max-w-full" />
                  ) : (
                    <span className="text-xs text-[#647087]">{it.file_name || "Media"}</span>
                  )}
                </div>
                <div className="text-sm font-medium text-white truncate">
                  {it.title || it.file_name || "Untitled"}
                </div>
                <div className="text-[10px] text-[#a0aabf] flex items-center gap-2 flex-wrap">
                  <span>From <span className="text-rose-200">{c?.name || "client"}</span></span>
                  <span>·</span>
                  <span>{it.uploaded_by_name || "portal user"}</span>
                  <span>·</span>
                  <span>{_fmt(it.created_at)}</span>
                </div>
                {it.notes && (
                  <div className="text-xs text-[#9aa3b5] line-clamp-2">{it.notes}</div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Link
                    to={`/customers/${it.customer_id}?tab=media`}
                    className="hv-btn hv-btn-ghost text-xs flex-1 justify-center"
                    data-testid={`portal-photo-open-${it.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Open in gallery
                  </Link>
                  <button
                    onClick={() => markSeen(it)}
                    disabled={busyId === it.id}
                    className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
                    data-testid={`portal-photo-seen-${it.id}`}
                  >
                    <CheckCheck className="w-3 h-3" /> Mark seen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
